import { useState } from "react";
import { useRouteContext } from "@tanstack/react-router";
import { TbUserSearch } from "react-icons/tb";
import { FiBookOpen, FiClipboard } from "react-icons/fi";
import { Input, InputChangeEvent } from "@progress/kendo-react-inputs";
import Card from "@/components/shared/Card";
import { ReviewSection, ReviewField } from "@/components/shared/ReviewSection";
import { formatDateOnly } from "@/utils/dates";
import { useCoreData } from "@/queries";
import { GlobalStateModel } from "@/models";
import {
  fetchRealCalpadsSyncData,
  CalpadsSyncData,
} from "@/queries/calpadsSyncApi";
import { useCalpadsSyncFieldTransformers } from "./useCalpadsSyncFieldTransformers";

const SSID_PATTERN = /^\d{10}$/;

export function CalpadsSyncLookupView() {
  const leaContext = useRouteContext({ from: "/$leaid" }) as GlobalStateModel;
  const coreData = useCoreData();
  const transformers = useCalpadsSyncFieldTransformers(coreData);

  const [ssid, setSsid] = useState("");
  const [validationError, setValidationError] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [result, setResult] = useState<CalpadsSyncData | null>(null);

  const handleChange = (value: string) => {
    setSsid(value);
    if (validationError) setValidationError("");
    if (searchError) setSearchError("");
    if (hasSearched) setHasSearched(false);
    if (result) setResult(null);
  };

  const validate = (): boolean => {
    if (!SSID_PATTERN.test(ssid.trim())) {
      setValidationError("Please enter a valid 10-digit SSID.");
      return false;
    }
    return true;
  };

  const handleSearch = async () => {
    if (!validate()) return;

    setIsLoading(true);
    setSearchError("");
    setHasSearched(true);

    try {
      const data = await fetchRealCalpadsSyncData(leaContext, ssid.trim());
      setResult(data);
    } catch (err) {
      setSearchError(
        err instanceof Error
          ? err.message
          : "An error occurred. Please try again."
      );
      setResult(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setSsid("");
    setValidationError("");
    setSearchError("");
    setHasSearched(false);
    setResult(null);
  };

  return (
    <>
      <Card className="card-custom mb-5 shadow-none">
        <div className="mb-0 p-4 bg-white w-100">
          <div className="row g-3">
            <div className="col-12 col-md-4">
              <label
                id="calpads-sync-ssid-label"
                htmlFor="calpads-sync-ssid"
                className="form-label fw-semibold"
              >
                <i className="bi bi-person-badge me-1" aria-hidden="true" />{" "}
                SSID
              </label>
              <Input
                id="calpads-sync-ssid"
                ariaLabelledBy="calpads-sync-ssid-label"
                style={{ width: "100%" }}
                value={ssid}
                onChange={(e: InputChangeEvent) =>
                  handleChange(e.value as string)
                }
                placeholder="Enter 10-digit SSID"
                disabled={isLoading}
                maxLength={10}
              />
            </div>

            {(validationError || searchError) && (
              <div className="col-12">
                <div
                  className="alert mb-0"
                  style={{
                    background: validationError ? "#fff3cd" : "#f8d7da",
                    border: `1px solid ${validationError ? "#ffc107" : "#f5c2c7"}`,
                    color: validationError ? "#856404" : "#842029",
                    borderRadius: "4px",
                    fontSize: "14px",
                    padding: "12px 16px",
                  }}
                >
                  {validationError || searchError}
                </div>
              </div>
            )}

            <div className="col-12 d-flex align-items-end gap-2 mt-4 mb-1">
              <button
                type="button"
                className="btn btn-primary d-flex align-items-center gap-2"
                onClick={handleSearch}
                disabled={isLoading}
              >
                <i className="bi bi-search" aria-hidden="true" />
                {isLoading ? "Searching..." : "Search"}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleClear}
                disabled={isLoading}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      </Card>

      {!hasSearched ? (
        <Card className="card-custom shadow-none">
          <div className="d-flex flex-column align-items-center justify-content-center py-5 text-center">
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                backgroundColor: "#e9ecef",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "1rem",
              }}
            >
              <TbUserSearch size={30} color="#6c757d" />
            </div>
            <h5 className="fw-semibold mb-2" style={{ color: "#0d2d4a" }}>
              Look Up CALPADS Sync Data
            </h5>
            <p
              className="mb-0"
              style={{ maxWidth: 380, fontSize: "0.9rem", color: "#212529" }}
            >
              Enter a student's SSID above, then click Search to see the ELAS
              and SPED data retrieved from CALPADS during the nightly sync.
            </p>
          </div>
        </Card>
      ) : isLoading ? (
        <Card className="card-custom shadow-none">
          <div className="d-flex align-items-center justify-content-center py-5 gap-3">
            <div
              className="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
              style={{ color: "#0d2d4a" }}
            />
            <span style={{ color: "#0d2d4a" }}>Searching...</span>
          </div>
        </Card>
      ) : searchError ? null : !result ? (
        <Card className="card-custom shadow-none">
          <div className="d-flex flex-column align-items-center justify-content-center py-5 text-center">
            <h5 className="fw-semibold mb-2" style={{ color: "#0d2d4a" }}>
              No CALPADS Sync Data Found
            </h5>
            <p
              className="mb-0"
              style={{ maxWidth: 380, fontSize: "0.9rem", color: "#212529" }}
            >
              No data was found for SSID "{ssid.trim()}". The SSID sync may not
              have returned data from CALPADS.
            </p>
          </div>
        </Card>
      ) : (
        <>
          <ReviewSection
            icon={<FiBookOpen />}
            title="English Language Acquisition"
          >
            {result.elas ? (
              <div className="row">
                <ReviewField
                  label="ELAS Status Code"
                  value={transformers.getElaStatusText(
                    result.elas.elaStatusCode
                  )}
                />
                <ReviewField
                  label="ELAS Start Date"
                  value={formatDateOnly(result.elas.elaStartDate)}
                />
                <ReviewField
                  label="Primary Language"
                  value={transformers.getPrimaryLanguageText(
                    result.elas.primaryLanguage
                  )}
                />
                <ReviewField
                  label="Correction Reason Code"
                  value={transformers.getCorrectionReasonText(
                    result.elas.correctionReasonCode
                  )}
                />
                <ReviewField
                  label="Grade Level Code"
                  value={result.elas.gradeLevelCode}
                />
              </div>
            ) : (
              <p className="mb-0 text-muted">
                No English Language Acquisition data found for this SSID.
              </p>
            )}
          </ReviewSection>

          <ReviewSection icon={<FiClipboard />} title="Special Education">
            {result.sped ? (
              <div className="row">
                <ReviewField
                  label="SPED Status"
                  value={transformers.getSpedStatusText(result.sped.spedStatus)}
                />
                <ReviewField
                  label="Student With Disability Start Date"
                  value={formatDateOnly(result.sped.swdsStartDate)}
                />
                <ReviewField
                  label="SPED Entry Start Date"
                  value={formatDateOnly(result.sped.swdsEntryStartDate)}
                />
                <ReviewField
                  label="Plan Type Code"
                  value={transformers.getSpedPlanTypeText(
                    result.sped.spedPlanTypeCode
                  )}
                />
                <ReviewField
                  label="Plan Start Date"
                  value={formatDateOnly(result.sped.planStartDate)}
                />
              </div>
            ) : (
              <p className="mb-0 text-muted">
                No Special Education data found for this SSID.
              </p>
            )}
          </ReviewSection>
        </>
      )}
    </>
  );
}
